import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

import {
  GoalsService,
  ObjectivesGridRequest,
  ObjectivesGridResponseData,
} from '../../../services/goals.service';

/**
 * Admin listing of the objectives catalog, with a per-row Retire action that archives the catalog
 * doc and cascade-expires every ACTIVE/PAUSED userObjective referencing it. Mirrors
 * plan-progressor-monitor.component.ts's grid/pagination pattern.
 */
@Component({
  selector: 'app-objectives-catalog',
  templateUrl: './objectives-catalog.component.html',
})
export class ObjectivesCatalogComponent extends AnyoComponent {
  constructor(
    private goalsSvc: GoalsService,
    auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastService,
    private modal: NzModalService,
  ) {
    super(auth);
  }

  tableLoading = false;
  page = 1;
  pageSize = 20;
  totalRecords = 0;
  pageSizeOptions = [20, 50, 100];
  data: ObjectivesGridResponseData[] = [];

  titleSearch = '';
  // Column filter on the Status header, not a toolbar checkbox — defaults to Active only (retired
  // excluded by default). Values are the raw IS_ARCHIVED filter values ('false'/'true').
  statusFilterValues: string[] = ['false'];

  private fetchRequestId = 0;

  async ready() {
    if (!this.user) return;
    const restored = this.restoreQueryFromUrl();
    if (restored) this.applyRequestToState(restored);
    this.fetch();
  }

  buildRequest(exportFlag = false): ObjectivesGridRequest {
    const req: ObjectivesGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
      filters: this.statusFilterValues.length
        ? { IS_ARCHIVED: this.statusFilterValues }
        : {},
    };
    if (this.titleSearch.trim()) {
      req.searchQuery = { TITLE: this.titleSearch.trim() };
    }
    return req;
  }

  onStatusFilterChange(values: string[]): void {
    this.statusFilterValues = values;
  }

  fetch(): void {
    this.tableLoading = true;
    const req = this.buildRequest(false);
    this.updateUrlWithQuery(req);
    const requestId = ++this.fetchRequestId;
    this.goalsSvc.getObjectivesGrid(req).subscribe({
      next: (res) => {
        if (requestId !== this.fetchRequestId) return;
        this.data = res.data;
        this.totalRecords = res.totalRecords;
        this.pageSize = res.pageSize;
        this.tableLoading = false;
      },
      error: (err) => {
        if (requestId !== this.fetchRequestId) return;
        this.tableLoading = false;
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(
          errorBody?.description ?? 'Failed to load objectives catalog',
        );
      },
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.fetch();
  }

  clearFilters(): void {
    this.titleSearch = '';
    this.statusFilterValues = ['false'];
    this.page = 1;
    this.fetch();
  }

  nzPageIndexChange(p: number): void {
    this.page = p;
    this.fetch();
  }

  nzPageSizeChange(s: number): void {
    this.pageSize = s;
    this.fetch();
  }

  /** Drill-down entry point into the userObjectives list — pre-filtered to this one objective, so
   * an admin never needs a separate top-level menu link to reach it unfiltered. */
  viewUsers(row: ObjectivesGridResponseData): void {
    const gridQuery = encodeURIComponent(
      JSON.stringify({
        page: 1,
        pageSize: 20,
        export: false,
        searchQuery: { OBJECTIVE_ID: row.objectiveId },
      }),
    );
    this.router.navigate(['/goals/user-objectives'], {
      queryParams: { gridQuery },
    });
  }

  retireAlert(row: ObjectivesGridResponseData): void {
    const impactLine = row.activeUserObjectiveCount
      ? `This will expire ${row.activeUserObjectiveCount} active/paused user goal(s) assigned to it.`
      : 'No users currently have this objective active or paused.';
    this.modal.confirm({
      nzTitle: `Retire "${row.title}"?`,
      nzContent: impactLine,
      nzOnOk: async () => {
        this.goalsSvc.retireObjective(row.objectiveId).subscribe({
          next: (res) => {
            this.toastrService.showSuccess(
              `Retired — ${res.expiredUserObjectivesCount} user goal(s) expired`,
            );
            this.fetch();
          },
          error: (err) => {
            const errorBody = err.error as IAnyoError;
            this.toastrService.showAnyoErrorToast(
              errorBody?.description ?? 'Failed to retire objective',
            );
          },
        });
      },
      nzOkText: 'Retire',
      nzCancelText: 'Cancel',
      nzClosable: false,
    });
  }

  private updateUrlWithQuery(req: ObjectivesGridRequest): void {
    const encoded = encodeURIComponent(JSON.stringify(req));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { gridQuery: encoded },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private restoreQueryFromUrl(): ObjectivesGridRequest | null {
    const qp = this.route.snapshot.queryParamMap.get('gridQuery');
    if (!qp) return null;
    try {
      const parsed: unknown = JSON.parse(decodeURIComponent(qp));
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
      }
      return parsed as ObjectivesGridRequest;
    } catch {
      return null;
    }
  }

  private applyRequestToState(req: ObjectivesGridRequest): void {
    if (typeof req.page === 'number') this.page = req.page;
    if (typeof req.pageSize === 'number') this.pageSize = req.pageSize;
    if (req.searchQuery?.TITLE) this.titleSearch = req.searchQuery.TITLE;
    this.statusFilterValues = req.filters?.IS_ARCHIVED ?? ['false'];
  }
}
